import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, Scale, Wallet, Briefcase, Building2, UserPlus, Filter,
  Activity, ShieldCheck, ArrowRight, Phone, Mail, MessageCircle,
  ExternalLink, Network, Sparkles, Bookmark, Clock, Pin,
} from "lucide-react";
import { Sidebar } from "../components/Sidebar";
import { MobileBottomNavSpacer } from "../components/MobileBottomNav";
import { PremiumInput } from "../components/brand/PremiumInput";
import {
  PILOT_PEOPLE,
  PILOT_PEOPLE_PINNED,
  PILOT_PEOPLE_RECENT,
  PILOT_PEOPLE_INSIGHTS,
} from "../data/pilotData";

/* iter-256 — People page operational refinement.
 *
 * Transforms the People page from a CRM-style contacts table into
 * a calm operational relationship workspace cohesive with Deal
 * Rooms + Forecasting Centre. The page reads as live transaction
 * coordination — soft operational lanes (not a spreadsheet) with
 * three columns per row:
 *
 *   PERSON           CURRENT MATTERS            OPERATIONAL CONTEXT
 *   avatar + name    compact property chips     "12 active deals"
 *   role + company   + stage pill               "Awaiting enquiries…"
 *
 * Right rail surfaces calm relationship intelligence (Network
 * overview · Quick insights · Pinned people · Recently
 * contacted). All routing, page shell, and sidebar stay untouched
 * — this is strictly a visual + operational refinement.
 *
 * Data calibration lives at the page level (same pattern as iter-
 * 249P Properties + iter-248/249 Forecasting Centre).
 */

const ROLE_GROUPS = [
  { id: "solicitor",      label: "Solicitors",      Icon: Scale },
  { id: "buyer",          label: "Buyers",          Icon: Wallet },
  { id: "seller",         label: "Sellers",         Icon: Briefcase },
  { id: "broker",         label: "Brokers",         Icon: Activity },
  { id: "surveyor",       label: "Surveyors",       Icon: ShieldCheck },
  { id: "managing_agent", label: "Managing Agents", Icon: Building2 },
  { id: "agent",          label: "Estate Agents",   Icon: Users },
  { id: "referrer",       label: "Referrers",       Icon: Sparkles },
  { id: "past_client",    label: "Past Clients",    Icon: Bookmark },
  { id: "prospect",       label: "Prospects",       Icon: UserPlus },
];

const ROLE_TABS = [
  { id: "all",            label: "All" },
  { id: "buyer",          label: "Buyers" },
  { id: "seller",         label: "Sellers" },
  { id: "solicitor",      label: "Solicitors" },
  { id: "broker",         label: "Brokers" },
  { id: "surveyor",       label: "Surveyors" },
  { id: "managing_agent", label: "Managing Agents" },
  { id: "agent",          label: "Estate Agents" },
  { id: "referrer",       label: "Referrers" },
  { id: "past_client",    label: "Past Clients" },
  { id: "prospect",       label: "Prospects" },
];

const STAGE_PILL_TONES = {
  "Under Offer":  "bg-amber-50 text-amber-700",
  "Pre-Exchange": "bg-violet-50 text-violet-700",
  "Conveyancing": "bg-sky-50 text-sky-700",
  "Exchanged":    "bg-emerald-50 text-emerald-700",
  "Marketing":    "bg-slate-50 text-slate-600",
  "Referring":    "bg-violet-50 text-violet-700",
  "Registered":   "bg-slate-50 text-slate-600",
  "Prospect":     "bg-slate-50 text-slate-600",
};

const BADGE_TONES = {
  "Highly responsive": "bg-emerald-50 text-emerald-700",
  "Fast responder":    "bg-emerald-50 text-emerald-700",
  "Exchange expert":   "bg-violet-50 text-violet-700",
  "Consistent":        "bg-emerald-50 text-emerald-700",
  "Active buyer":      "bg-emerald-50 text-emerald-700",
  "Engaged":           "bg-emerald-50 text-emerald-700",
  "New contact":       "bg-slate-100 text-slate-600",
  "Watch":             "bg-amber-50 text-amber-700",
  // iter-366B — extended CRM roles.
  "Past client":       "bg-slate-100 text-slate-600",
  "Prospect":          "bg-sky-50 text-sky-700",
  "Referrer":          "bg-violet-50 text-violet-700",
  "Standard":          "bg-slate-100 text-slate-600",
};

const CONTEXT_TONES = {
  amber:   { container: "",                    title: "text-amber-700"  },
  green:   { container: "",                    title: "text-emerald-700" },
  rose:    { container: "",                    title: "text-rose-700"   },
  neutral: { container: "",                    title: "text-slate-700"  },
};

/* iter-270I — Single source of truth.
 *
 * The People page no longer carries its own people roster. Rows are
 * derived from `PILOT_PEOPLE`, which itself folds every PILOT_DEAL's
 * `participants` array into one canonical record per (role, name).
 * That guarantees the SAME solicitors / brokers / managing agent
 * appear across Deal Rooms · Documents Hub · Forecasting Centre ·
 * Request Document recipient picker · People page. Adding a new
 * participant in pilotData.js automatically surfaces them here.
 *
 * The right-rail Pinned / Recent / Insights vary by composition,
 * but always read from the same single-source aggregations so the
 * page never drifts from the underlying transaction state. */
const _PILOT_PEOPLE = PILOT_PEOPLE;
// Icon name → component, so canonical insights can ship as plain data.
const _ICON_LOOKUP = { Users, Activity };
const _PILOT_INSIGHTS = PILOT_PEOPLE_INSIGHTS.map((it) => ({
  ...it,
  icon: _ICON_LOOKUP[it.icon] || Users,
}));
const _PILOT_PINNED = PILOT_PEOPLE_PINNED;
const _PILOT_RECENT = PILOT_PEOPLE_RECENT;

const initials = (name) => {
  const src = (name || "?").split(/\s+/).slice(0, 2);
  return src.map((p) => (p ? p[0] : "")).join("").toUpperCase() || "?";
};

// iter-alignment · P44 — Renamed from PropertyChip → MatterCard.
// Each Current Matter is now a two-line, clickable card that opens
// its Deal Room directly.  The subline surfaces the operational
// responsibility signal from the SAME SSOT the rail uses
// (blockerState / stage) so operators triage without opening every
// card.
function MatterCard({ prop, stage, dealId, responsibility, personId, testid }) {
  const tone = STAGE_PILL_TONES[stage] || STAGE_PILL_TONES["Under Offer"];
  return (
    <Link
      to={`/deal-rooms/${dealId}`}
      className="group flex items-center gap-2 min-w-0 rounded-lg -mx-1 px-1 py-1 hover:bg-slate-50/80 transition-colors"
      data-testid={testid || `people-row-matter-${personId}-${dealId}`}
    >
      <span className="inline-flex items-center justify-center h-6 w-6 rounded-md bg-slate-100 shrink-0 group-hover:bg-white group-hover:ring-1 group-hover:ring-slate-200">
        <Building2 className="h-3 w-3 text-slate-400" strokeWidth={2.2} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[12.5px] font-medium text-slate-800 truncate group-hover:text-slate-900" title={prop}>{prop}</span>
          <span className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-md ${tone} text-[10px] font-bold tracking-[0.06em]`}>
            {stage}
          </span>
        </div>
        {responsibility && (
          <div className="mt-0.5 text-[11px] text-slate-500 truncate" title={responsibility}>
            {responsibility}
          </div>
        )}
      </div>
    </Link>
  );
}

function ActionIcon({ Icon, label, testid, href, onClick }) {
  const baseCls = "inline-flex items-center justify-center h-7 w-7 rounded-full bg-white ring-1 ring-slate-200/60 text-slate-500 hover:bg-slate-50 hover:text-slate-700 cf-press";
  const disabledCls = "inline-flex items-center justify-center h-7 w-7 rounded-full bg-white/60 ring-1 ring-slate-200/40 text-slate-300 cursor-not-allowed";
  if (href) {
    return (
      <a
        href={href}
        onClick={(e) => { e.stopPropagation(); onClick?.(e); }}
        className={baseCls}
        data-testid={testid}
        aria-label={label}
        title={label}
        target={href.startsWith("http") ? "_blank" : undefined}
        rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
      >
        <Icon className="h-3 w-3" strokeWidth={2} />
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(e); }}
      disabled={!onClick}
      className={onClick ? baseCls : disabledCls}
      data-testid={testid}
      aria-label={label}
      title={label}
    >
      <Icon className="h-3 w-3" strokeWidth={2} />
    </button>
  );
}

function PersonRow({ person }) {
  const ctx = CONTEXT_TONES[person.ctxTone] || CONTEXT_TONES.neutral;
  const badgeTone = BADGE_TONES[person.badge] || BADGE_TONES["Standard"];
  return (
    <div
      className="grid grid-cols-[minmax(0,1.1fr)_minmax(0,1.5fr)_minmax(0,1fr)_auto] items-start gap-6 py-5 border-t border-slate-100/80 first:border-t-0"
      data-testid={`people-row-${person.id}`}
    >
      {/* 1 · PERSON */}
      <div className="flex items-start gap-3 min-w-0">
        <div className="h-9 w-9 rounded-full bg-violet-100 text-violet-700 inline-flex items-center justify-center text-[12px] font-bold shrink-0">
          {initials(person.name)}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <Link
              to={`/contacts/${person.id}`}
              className="text-[14px] font-semibold text-slate-900 truncate hover:text-violet-700 hover:underline underline-offset-2"
              title={person.name}
              data-testid={`people-row-name-${person.id}`}
            >
              {person.name}
            </Link>
            {person.active && (
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" aria-label="Active" />
            )}
            {/* iter-alignment · P44 — Compact "X Active" badge
                sits alongside the person's name so operators see
                pipeline weight at a glance.  Hidden when zero
                live matters (the row falls back to the most
                recent completed matter instead). */}
            {person.activeCount > 0 && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[10.5px] font-bold tracking-[0.04em] ring-1 ring-emerald-200/70 shrink-0 tabular-nums"
                data-testid={`people-row-active-count-${person.id}`}
              >
                {person.activeCount} Active
              </span>
            )}
            <span className={`inline-flex items-center px-2 py-0.5 rounded-md ${badgeTone} text-[10.5px] font-semibold shrink-0`}
                  data-testid={`people-row-badge-${person.id}`}>
              {person.badge}
            </span>
          </div>
          <div className="mt-0.5 text-[11.5px] text-slate-500 truncate">
            {person.roleLabel}{person.company ? ` · ${person.company}` : ""}
          </div>
          {(person.phone || person.email) && (
            <div
              className="mt-0.5 text-[10.5px] text-slate-400 truncate tabular-nums"
              data-testid={`people-row-contact-${person.id}`}
              title={[person.phone, person.email].filter(Boolean).join(" · ")}
            >
              {person.phone && <span data-testid={`people-row-phone-${person.id}`}>{person.phone}</span>}
              {person.phone && person.email && <span className="mx-1 text-slate-300">·</span>}
              {person.email && <span data-testid={`people-row-email-address-${person.id}`}>{person.email}</span>}
            </div>
          )}
        </div>
      </div>

      {/* 2 · CURRENT MATTERS
          iter-alignment · P44 — Renamed from "Active Transactions".
          Each row is now a two-line MatterCard linking to the Deal
          Room; the empty-state copy reads "No active matters" only
          when zero live/completed matters exist. */}
      <div className="min-w-0" data-testid={`people-row-matters-${person.id}`}>
        {person.deals.length === 0 ? (
          <div className="text-[12.5px] text-slate-400 italic">No active matters</div>
        ) : (
          <div className="space-y-1.5">
            {person.deals.map((d) => (
              <MatterCard
                key={`${person.id}-m-${d.dealId}`}
                prop={d.prop}
                stage={d.stage}
                dealId={d.dealId}
                responsibility={d.responsibility}
                personId={person.id}
              />
            ))}
            {person.moreDeals > 0 && (
              <Link
                to={`/contacts/${person.id}`}
                className="inline-block text-[11.5px] font-semibold text-violet-600 hover:text-violet-700 pt-0.5"
                data-testid={`people-row-more-matters-${person.id}`}
              >
                +{person.moreDeals} more {person.moreDeals === 1 ? "matter" : "matters"}
              </Link>
            )}
          </div>
        )}
      </div>

      {/* 3 · OPERATIONAL CONTEXT */}
      <div className="min-w-0" data-testid={`people-row-context-${person.id}`}>
        <div className={`text-[13px] font-semibold leading-tight ${ctx.title}`}>
          {person.ctxTitle}
        </div>
        {person.ctxNote && (
          <div className={`mt-1 inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold ${
            person.ctxTone === "amber" ? "bg-amber-50 text-amber-700"
            : person.ctxTone === "rose" ? "bg-rose-50 text-rose-700"
            : person.ctxTone === "green" ? "bg-emerald-50 text-emerald-700"
            : "bg-slate-50 text-slate-600"
          }`}>
            {person.ctxNote}
          </div>
        )}
        <div className="mt-1 text-[11px] text-slate-400">{person.ctxLast}</div>
      </div>

      {/* 4 · ACTION ICONS */}
      <div className="flex items-center gap-1.5" data-testid={`people-row-actions-${person.id}`}>
        <ActionIcon
          Icon={Phone}
          label={person.phone ? `Call ${person.phone}` : "Call"}
          testid={`people-row-call-${person.id}`}
          href={person.phone ? `tel:${person.phone.replace(/\s+/g, "")}` : null}
        />
        <ActionIcon
          Icon={Mail}
          label={person.email ? `Email ${person.email}` : "Email"}
          testid={`people-row-email-${person.id}`}
          href={person.email ? `mailto:${person.email}` : null}
        />
        <ActionIcon
          Icon={MessageCircle}
          label="Message"
          testid={`people-row-message-${person.id}`}
          onClick={() => { /* future: opens messaging composer */ }}
        />
        <ActionIcon
          Icon={ExternalLink}
          label={person.deals?.[0]?.dealId ? "Open first active deal" : "Open"}
          testid={`people-row-open-${person.id}`}
          href={person.deals?.[0]?.dealId ? `/deal-rooms/${person.deals[0].dealId}` : null}
        />
      </div>
    </div>
  );
}

function NetworkOverviewCard({ counts }) {
  const tiles = [
    { id: "total",       value: counts.total,          label: "Total people",        tone: "bg-violet-50 text-violet-700",  icon: Network },
    { id: "active",      value: counts.activeThisWeek, label: "Active this week",    tone: "bg-emerald-50 text-emerald-700", icon: Activity },
    { id: "exchange",    value: counts.exchangeReady,  label: "Exchange ready",      tone: "bg-violet-50 text-violet-700",  icon: ShieldCheck },
    { id: "delaying",    value: counts.delaying,       label: "Delaying exchanges",  tone: "bg-rose-50 text-rose-700",      icon: Clock },
  ];
  return (
    <div className="rounded-2xl bg-white ring-1 ring-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-4" data-testid="people-rail-network-overview">
      <div className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.10em] text-slate-500 mb-3">
        <Network className="h-3 w-3 text-violet-500" /> Network overview
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <div key={t.id} className={`rounded-xl ${t.tone.split(" ").includes("bg-rose-50") ? "bg-rose-50" : t.tone.split(" ").includes("bg-emerald-50") ? "bg-emerald-50" : "bg-violet-50"} px-3 py-2.5`}>
              <div className="inline-flex items-center gap-1.5">
                <Icon className={`h-3.5 w-3.5 ${t.tone.split(" ").includes("text-rose-700") ? "text-rose-600" : t.tone.split(" ").includes("text-emerald-700") ? "text-emerald-600" : "text-violet-600"}`} strokeWidth={2.2} />
                <span className={`text-[20px] font-bold tabular-nums leading-none ${t.tone.split(" ").includes("text-rose-700") ? "text-rose-700" : t.tone.split(" ").includes("text-emerald-700") ? "text-emerald-700" : "text-violet-700"}`}>{t.value}</span>
              </div>
              <div className="mt-1 text-[10.5px] text-slate-500 leading-tight">{t.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function PeoplePage({ embedded = false }) {
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [activeWeek, setActiveWeek] = useState(false);
  const [exchangeReady, setExchangeReady] = useState(false);

  const allPeople = _PILOT_PEOPLE;
  const filtered = useMemo(() => {
    let rows = allPeople;
    if (tab !== "all") rows = rows.filter((p) => p.role === tab);
    if (activeWeek)    rows = rows.filter((p) => p.active);
    if (exchangeReady) rows = rows.filter((p) => p.ctxTitle.toLowerCase().includes("exchange ready"));
    if (q) {
      const t = q.toLowerCase();
      rows = rows.filter((p) =>
        p.name.toLowerCase().includes(t) ||
        (p.company || "").toLowerCase().includes(t) ||
        p.roleLabel.toLowerCase().includes(t),
      );
    }
    return rows;
  }, [allPeople, tab, q, activeWeek, exchangeReady]);

  const counts = useMemo(() => {
    const c = { total: allPeople.length, activeThisWeek: 0, exchangeReady: 0, delaying: 0 };
    allPeople.forEach((p) => {
      if (p.active) c.activeThisWeek += 1;
      if (p.ctxTitle.toLowerCase().includes("exchange ready")) c.exchangeReady += 1;
      if (p.ctxNote && /awaiting|delay|searching/i.test(p.ctxNote)) c.delaying += 1;
    });
    return c;
  }, [allPeople]);

  const groups = useMemo(() => {
    return ROLE_GROUPS.map((g) => ({
      ...g,
      rows: filtered.filter((p) => p.role === g.id),
    })).filter((g) => g.rows.length > 0);
  }, [filtered]);

  const body = (
    <div className={embedded ? "space-y-6" : "px-4 md:px-10 py-6 md:py-9"}>
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-7">
        {/* ── Main column ────────────────────────────────────────── */}
        <div className="space-y-7 min-w-0">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <h1 className="text-[32px] font-bold text-slate-900 tracking-[-0.01em] leading-none" data-testid="people-page-title">People</h1>
              <p className="text-[13px] text-slate-500 mt-2">Everyone currently involved in live transactions.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button type="button" className="inline-flex items-center gap-2 rounded-xl bg-white ring-1 ring-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] px-3.5 py-2 text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50 cf-press" data-testid="people-filter-btn">
                <Filter className="h-3.5 w-3.5 text-slate-500" /> Filter
              </button>
              <button
                type="button"
                onClick={() => setActiveWeek((v) => !v)}
                aria-pressed={activeWeek}
                className={`inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-[12.5px] font-semibold cf-press transition ${
                  activeWeek
                    ? "bg-violet-50 text-violet-700 ring-1 ring-violet-200"
                    : "bg-white ring-1 ring-slate-200/70 text-slate-700 hover:bg-slate-50 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
                }`}
                data-testid="people-toggle-active-week"
              >
                <Activity className="h-3.5 w-3.5" /> Active this week
              </button>
              <button
                type="button"
                onClick={() => setExchangeReady((v) => !v)}
                aria-pressed={exchangeReady}
                className={`inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-[12.5px] font-semibold cf-press transition ${
                  exchangeReady
                    ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                    : "bg-white ring-1 ring-slate-200/70 text-slate-700 hover:bg-slate-50 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
                }`}
                data-testid="people-toggle-exchange-ready"
              >
                <ShieldCheck className="h-3.5 w-3.5" /> Exchange ready
              </button>
              <button type="button" className="inline-flex items-center gap-2 rounded-xl bg-violet-600 hover:bg-violet-700 px-4 py-2 text-[13px] font-semibold text-white shadow-[0_1px_2px_rgba(124,58,237,0.18),0_6px_14px_-8px_rgba(124,58,237,0.4)] cf-press" data-testid="people-add-btn">
                <UserPlus className="h-3.5 w-3.5" /> Add Person
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="max-w-xl">
            <PremiumInput format="search" value={q} onChange={setQ} placeholder="Search people, company or role…" clearable testid="people-search" />
          </div>

          {/* Role tabs */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="people-tabs">
            {ROLE_TABS.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={`inline-flex items-center px-4 py-2 rounded-xl text-[12.5px] font-semibold transition ${
                    active
                      ? "bg-violet-600 text-white shadow-[0_1px_2px_rgba(124,58,237,0.18),0_6px_14px_-8px_rgba(124,58,237,0.4)]"
                      : "bg-white ring-1 ring-slate-200/70 text-slate-700 hover:bg-slate-50 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
                  }`}
                  data-testid={`people-tab-${t.id}`}
                  aria-pressed={active}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Grouped lanes */}
          {groups.length === 0 && (
            <div className="rounded-2xl bg-white ring-1 ring-slate-200/70 px-6 py-12 text-center text-[13px] text-slate-500">
              No people match the current filters.
            </div>
          )}
          {groups.map((g) => {
            const Icon = g.Icon;
            return (
              <section key={g.id} data-testid={`people-group-${g.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="inline-flex items-center gap-2 text-[14px] font-bold text-slate-900">
                    <Icon className="h-4 w-4 text-violet-500" strokeWidth={2.2} />
                    {g.label}
                    <span className="text-[12.5px] font-semibold text-slate-400 ml-1">{g.rows.length}</span>
                  </div>
                  <Link to="/people" className="inline-flex items-center gap-1 text-[12px] font-semibold text-violet-600 hover:text-violet-700">
                    View all <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
                <div className="rounded-2xl bg-white ring-1 ring-slate-200/60 shadow-[0_1px_2px_rgba(15,23,42,0.03)] px-6">
                  {g.rows.map((p) => (
                    <PersonRow key={p.id} person={p} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        {/* ── Right rail ─────────────────────────────────────────── */}
        <aside className="space-y-4 min-w-0" data-testid="people-right-rail">
          <NetworkOverviewCard counts={counts} />

          {/* Quick insights */}
          <div className="rounded-2xl bg-white ring-1 ring-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-4" data-testid="people-rail-insights">
            <div className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.10em] text-slate-500 mb-3">
              <Sparkles className="h-3 w-3 text-violet-500" /> Quick insights
            </div>
            <div className="space-y-3">
              {_PILOT_INSIGHTS.map((it, i) => {
                const Icon = it.icon;
                const dotTone = it.tone === "violet" ? "bg-violet-100 text-violet-600" : it.tone === "amber" ? "bg-amber-100 text-amber-600" : "bg-rose-100 text-rose-600";
                return (
                  <div key={i} className="flex items-start gap-3" data-testid={`people-rail-insight-${i}`}>
                    <div className={`h-7 w-7 rounded-lg ${dotTone} inline-flex items-center justify-center shrink-0`}>
                      <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[12.5px] font-semibold text-slate-800 leading-tight">{it.title}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{it.cta}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <Link to="/insights" className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-violet-600 hover:text-violet-700">
              View all insights <ArrowRight className="h-3 w-3" />
            </Link>
          </div>

          {/* Pinned people */}
          <div className="rounded-2xl bg-white ring-1 ring-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-4" data-testid="people-rail-pinned">
            <div className="flex items-center justify-between mb-3">
              <div className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.10em] text-slate-500">
                <Bookmark className="h-3 w-3 text-violet-500" /> Pinned people
              </div>
              <button type="button" className="text-[11.5px] font-semibold text-violet-600 hover:text-violet-700" data-testid="people-rail-pinned-manage">Manage</button>
            </div>
            <div className="space-y-2.5">
              {_PILOT_PINNED.map((p) => (
                <div key={p.id} className="flex items-center gap-3" data-testid={`people-rail-pinned-${p.id}`}>
                  <div className="h-7 w-7 rounded-full bg-violet-100 text-violet-700 inline-flex items-center justify-center text-[11px] font-bold">
                    {initials(p.name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-semibold text-slate-800 truncate">{p.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{p.company}</div>
                  </div>
                  <Pin className="h-3 w-3 text-violet-400" />
                </div>
              ))}
            </div>
            <Link to="/people?filter=pinned" className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-violet-600 hover:text-violet-700">
              View all pinned <ArrowRight className="h-3 w-3" />
            </Link>
          </div>

          {/* Recently contacted */}
          <div className="rounded-2xl bg-white ring-1 ring-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-4" data-testid="people-rail-recent">
            <div className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.10em] text-slate-500 mb-3">
              <Clock className="h-3 w-3 text-violet-500" /> Recently contacted
            </div>
            <div className="space-y-2.5">
              {_PILOT_RECENT.map((p) => (
                <div key={p.id} className="flex items-center gap-3" data-testid={`people-rail-recent-${p.id}`}>
                  <div className="h-7 w-7 rounded-full bg-violet-100 text-violet-700 inline-flex items-center justify-center text-[11px] font-bold">
                    {initials(p.name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-semibold text-slate-800 truncate">{p.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{p.when}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <ActionIcon Icon={Phone}         label="Call"    testid={`people-rail-recent-call-${p.id}`} />
                    <ActionIcon Icon={MessageCircle} label="Message" testid={`people-rail-recent-message-${p.id}`} />
                  </div>
                </div>
              ))}
            </div>
            <Link to="/activity" className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-violet-600 hover:text-violet-700">
              View all activity <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </aside>
      </div>
      <MobileBottomNavSpacer />
    </div>
  );

  if (embedded) return <div data-testid="people-main">{body}</div>;
  return (
    <div className="min-h-screen" style={{ background: "var(--cf-app-bg)" }}>
      <Sidebar />
      <main className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out flex flex-col min-h-screen" data-testid="people-main">
        {body}
      </main>
    </div>
  );
}
